import type { PageContent } from "../types";

export const p_july_2026_google_core_update_recovery_playbook: PageContent = {
  slug: "july-2026-google-core-update-recovery-playbook",
  title: "July 2026: Google Core and Spam Update Recovery Playbook",
  description:
    "A practical July 2026 review of Google’s confirmed May core and June spam updates, with a measured Search Console diagnosis and recovery plan.",
  date: "2026-07-22",
  body: `# July 2026: Google core and spam update recovery playbook

**Published:** July 22, 2026

## TL;DR

Google’s official Search Status Dashboard shows two recent ranking updates: the **May 2026 core update**, completed on 2 June, and the **June 2026 spam update**, completed on 26 June. There is no confirmed July 2026 core update as of this article’s publication date.

If visibility changed around those periods, confirm the timing, separate technical and demand changes from ranking changes, and improve only where the evidence is strong. Google recommends waiting at least a full week after a core update finishes before comparing Search Console performance.

## What Google actually confirmed

The [Google Search Status Dashboard](https://status.search.google.com/products/rGHU1u87FJnkP6W2GwMi/history) records:

- **May 2026 core update:** 21 May to 2 June 2026.
- **June 2026 spam update:** 24 to 26 June 2026, applied globally and to all languages.

That distinction matters. A core update broadly reassesses how useful and reliable results are. A spam update is tied to systems that identify practices conflicting with Google’s spam policies. A traffic change near either period is a reason to investigate, not proof that the update caused it.

## Before changing the website

### Confirm the rollout window

Use the official dashboard rather than a third-party update name. Compare settled periods and avoid drawing conclusions from the middle of a rollout.

### Check whether the loss is real and sustained

In Search Console, compare equivalent date ranges and review Web, Image, Video, and News performance separately. Look at clicks, impressions, position, click-through rate, pages, and queries together.

### Rule out other causes

Check analytics changes, server availability, robots rules, indexing, redirects, canonicals, site migrations, seasonality, and changes in search demand. Google’s [traffic-drop diagnosis guide](https://developers.google.com/search/docs/monitor-debug/debugging-search-traffic-drops) explains how different causes can produce different patterns.

## Diagnose by page group, not panic

Group affected URLs into useful templates: service pages, location pages, product categories, articles, and brand searches. Then ask:

- Did the whole group move, or only one page?
- Did impressions fall, or did clicks fall while visibility stayed similar?
- Has the apparent intent of the result page changed?
- Are several pages from the site competing for the same job?
- Do current competitors answer the decision more clearly or provide stronger evidence?

This gives you a more defensible action list than rewriting every page that had a bad week.

## A four-week recovery plan

### Week 1 — Establish evidence

Export Search Console performance, annotate the confirmed update dates, check analytics and conversion tracking, and crawl the affected routes. Record the baseline before editing.

### Week 2 — Review the largest sustained losses

Compare the affected pages with current search intent. Check usefulness, service clarity, duplication, internal links, proof, author information, and technical access. Prioritise the pages closest to meaningful enquiries or sales.

### Week 3 — Make controlled improvements

Fix confirmed technical blockers and improve a small set of pages where the diagnosis is clear. Avoid changing a URL, title, page structure, and targeting all at once unless a consolidation genuinely requires it.

### Week 4 — Validate and monitor

Crawl the updated pages, test their internal links and canonicals, annotate the release, and allow time for recrawling. Google’s [core update guidance](https://developers.google.com/search/docs/appearance/core-updates) says improvements may take days to several months to show and do not guarantee a ranking change.

## Where to focus after a core update

### People-first commercial content

Service and product pages should help a buyer understand scope, fit, proof, process, and the next step. Adding generic paragraphs for word count is not a meaningful quality improvement.

### Distinct location and sector pages

Keep location pages only where they can provide useful, specific information. Near-duplicate pages with a town or industry name swapped into the same template create weak choices for users and can split internal relevance.

### Clear internal architecture

Important services should be linked from relevant hubs, navigation, and supporting content using descriptive anchors. Fix internal links that pass through redirects and avoid orphaning deeper pages.

### Evidence and accountability

Make it clear who created the content, why their experience is relevant, when material information was updated, and how a visitor can verify or act on the advice.

## What to review after a spam update

Read Google’s [spam policies](https://developers.google.com/search/docs/essentials/spam-policies) directly. Review scaled low-value content, doorway pages, manipulative link practices, hacked content, cloaking, misleading functionality, and other policy risks. Do not assume ordinary page-quality work will resolve a policy problem, and do not make a speculative link disavowal without evidence.

## What not to do

- Do not publish large numbers of lightly differentiated pages to replace lost traffic.
- Do not delete useful pages as a first response.
- Do not rewrite content that is still serving users and performing well.
- Do not treat an automated audit score as proof of an algorithmic cause.
- Do not promise a fixed recovery date or guaranteed ranking outcome.

## When an independent audit helps

If technical faults, overlapping pages, and content-quality questions are mixed together, a [bespoke SEO website audit](/seo/bespoke-seo-website-audits "Bespoke SEO website audit") can turn the evidence into a prioritised implementation plan.

For a broader explanation of update diagnosis, read [Understanding Google’s latest algorithm update](/blog/understanding-googles-latest-algorithm-update "Understanding Google’s latest algorithm update"). To discuss a sustained visibility loss, [book an SEO consultation](/contact "Book an SEO consultation").

## Key takeaways

- The confirmed updates were the May 2026 core update and June 2026 spam update.
- Verify dates and causes before changing the site.
- Diagnose sustained losses by page group, query intent, and traffic type.
- Make controlled, useful improvements and record what changed.
- Expect recrawling and reassessment to take time; recovery is never guaranteed.
`,
};
